import React, { lazy, Suspense, useMemo } from 'react';
import { type IconName, iconNames } from './icons.gen';

export type IconSize = 'sm' | 'md' | 'lg' | number;

const sizeMap: Record<Exclude<IconSize, number>, number> = {
  sm: 14,
  md: 16,
  lg: 18,
};

type IconProps = Omit<React.SVGProps<SVGSVGElement>, 'color'> & {
  children: React.ReactElement<React.SVGProps<SVGSVGElement>>;
  size?: IconSize;
  color?: string;
  title?: string;
  className?: string;
};

function Icon({ children, size = 'md', color, title, className, ...rest }: IconProps) {
  const pixel = typeof size === 'number' ? size : sizeMap[size];
  const a11yProps = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true };

  const svg = useMemo(
    () =>
      React.cloneElement(children, {
        width: rest.width ?? pixel,
        height: rest.height ?? pixel,
        focusable: false,
        fill: color,
        ...a11yProps,
        ...rest,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [children, pixel, title, color, className]
  );

  return (
    <span {...a11yProps} className={className} style={color ? { color } : undefined}>
      {svg}
    </span>
  );
}

export type { IconName };

const modules = import.meta.glob<{
  default: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}>('../../assets/icons/*.svg', {
  query: { react: '' },
  eager: false,
});

const lazyIcons = Object.fromEntries(
  iconNames.map((name) => [
    name,
    lazy(async () => {
      const importer = modules[`../../assets/icons/${name}.svg`];
      const mod = await importer();
      return { default: mod.default };
    }),
  ])
) as Record<IconName, React.LazyExoticComponent<React.ComponentType<React.SVGProps<SVGSVGElement>>>>;

export type DynamicIconProps = Omit<IconProps, 'children'> & {
  name: IconName;
  fallback?: React.ReactNode;
};

export function DynamicIcon({
  name,
  size = 'md',
  color,
  title,
  className,
  fallback,
  ...rest
}: DynamicIconProps) {
  const LazyIcon = lazyIcons[String(name)];
  if (!LazyIcon) {
    if (import.meta.env.DEV) {
      console.warn(`[DynamicIcon] icon not found: ${String(name)}`);
    }
    return null;
  }
  const pixel = typeof size === 'number' ? size : sizeMap[size];
  const defaultFallback = (
    <span
      aria-hidden
      className={className}
      style={{ display: 'inline-block', width: pixel, height: pixel }}
    />
  );
  return (
    <Suspense fallback={fallback ?? defaultFallback}>
      <Icon size={size} color={color} title={title} className={className} {...rest}>
        <LazyIcon />
      </Icon>
    </Suspense>
  );
}
